import type { GoalAlertEvent } from "@/hooks/useTickerGoalEvents";
import type { TickerItem } from "@/hooks/useLiveTickerData";

interface GoalTickerItemProps {
  goal: GoalAlertEvent;
  tickerItems: TickerItem[];
}

function TeamLogo({ src, alt }: { src: string; alt: string }) {
  if (!src) return null;
  return (
    <img
      src={src}
      alt={alt}
      className="w-4 h-4 object-contain flex-shrink-0"
      width={16}
      height={16}
      loading="lazy"
      decoding="async"
      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
    />
  );
}

export function GoalTickerItem({ goal, tickerItems }: GoalTickerItemProps) {
  const match = tickerItems.find((t) => t.fixture_id === goal.fixture_id);

  // Determine which side scored
  const scoringSide: 'home' | 'away' | null = match?.game
    ? goal.team_id === match.game.home_team?.id ? 'home'
    : goal.team_id === match.game.away_team?.id ? 'away'
    : null
    : null;

  return (
    <div className="flex items-center justify-center gap-3 h-10 w-full animate-[goal-badge-pulse_0.8s_ease-in-out_infinite]">
      {/* GOAL badge — flashes yellow↔red */}
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-[hsl(var(--sg7-gold))] text-[hsl(var(--sg7-surface-1))] animate-[goal-badge-flash_0.4s_step-end_infinite]">
        <span className="text-sm">⚽</span>
        GOAL
      </span>

      {/* Match info */}
      {match ? (
        <span className="inline-flex items-center gap-1.5 text-[14px] font-black text-[hsl(var(--sg7-text))]">
          <TeamLogo src={match.home_team_logo} alt={match.home_team} />
          {match.home_team}
          <span className="mx-1 text-[hsl(var(--sg7-gold))] font-black">
            <span className={scoringSide === 'home' ? 'inline-block animate-[score-digit-pulse_0.6s_ease-in-out_infinite]' : ''}>
              {match.home_score}
            </span>
            –
            <span className={scoringSide === 'away' ? 'inline-block animate-[score-digit-pulse_0.6s_ease-in-out_infinite]' : ''}>
              {match.away_score}
            </span>
          </span>
          {match.away_team}
          <TeamLogo src={match.away_team_logo} alt={match.away_team} />
        </span>
      ) : (
        <span className="text-[14px] font-bold text-[hsl(var(--sg7-text))]">
          Goal!
        </span>
      )}

      {/* Scorer + minute */}
      <span className="text-[12px] font-bold text-[hsl(var(--sg7-text-secondary))]">
        {goal.player_name && (
          <span className="text-[hsl(var(--sg7-gold))]">{goal.player_name} </span>
        )}
        {goal.minute > 0 && <span>{goal.minute}'</span>}
      </span>
    </div>
  );
}
